/**
 * i18n 配置和实现
 * 支持动态加载语言资源、语言变体继承、RTL布局等
 */

// 语言配置
export const localeConfig = {
  // 默认语言
  defaultLocale: 'zh-CN',

  // 语言变体继承关系（子语言继承父语言）
  localeHierarchy: {
    'en-US': 'en',
    'en-GB': 'en',
    'zh-TW': 'zh-CN',
    'zh-HK': 'zh-CN',
    'es-MX': 'es',
    'fr-CA': 'fr',
    'pt-BR': 'pt',
    'de-AT': 'de',
  },

  // RTL 语言列表
  rtlLocales: ['ar', 'ar-SA', 'he', 'fa'],

  // 支持的语言列表
  supportedLocales: [
    { code: 'zh-CN', name: '简体中文', nativeName: '简体中文' },
    { code: 'zh-TW', name: '繁体中文 (台湾)', nativeName: '繁體中文' },
    { code: 'zh-HK', name: '繁体中文 (香港)', nativeName: '繁體中文' },
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'en-US', name: 'English (US)', nativeName: 'English (US)' },
    { code: 'en-GB', name: 'English (UK)', nativeName: 'English (UK)' },
    { code: 'es', name: 'Español (ES)', nativeName: 'Español' },
    { code: 'es-MX', name: 'Español (MX)', nativeName: 'Español (México)' },
    { code: 'fr', name: 'Français (FR)', nativeName: 'Français' },
    { code: 'fr-CA', name: 'Français (CA)', nativeName: 'Français (Canada)' },
    { code: 'de', name: 'Deutsch (DE)', nativeName: 'Deutsch' },
    { code: 'de-AT', name: 'Deutsch (AT)', nativeName: 'Deutsch (Österreich)' },
    { code: 'ru', name: 'Русский', nativeName: 'Русский' },
    { code: 'ja', name: '日本語', nativeName: '日本語' },
    { code: 'ar', name: 'العربية', nativeName: 'العربية' },
    { code: 'hi', name: 'हिन्दी', nativeName: 'हिन्दी' },
    { code: 'pt', name: 'Português (PT)', nativeName: 'Português' },
    { code: 'pt-BR', name: 'Português (BR)', nativeName: 'Português (Brasil)' },
  ],
};

// 语言资源缓存
const localeCache: Record<string, any> = {};

// 当前语言
let currentLocale: string = localeConfig.defaultLocale;

// 合并语言资源（支持语言变体继承）
function mergeLocaleResources(locale: string, resources: any): any {
  // 查找父语言
  const parentLocale = localeConfig.localeHierarchy[locale as keyof typeof localeConfig.localeHierarchy];
  
  if (parentLocale && localeCache[parentLocale]) {
    // 子语言继承父语言的资源
    return {
      ...localeCache[parentLocale],
      ...resources,
    };
  }
  
  return resources;
}

// 动态加载语言资源
export async function loadLocaleResources(locale: string): Promise<any> {
  // 检查缓存
  if (localeCache[locale]) {
    return localeCache[locale];
  }

  try {
    // 尝试加载模块
    const module = await import(`../locales/${locale}/common.json`);
    const resources = module.default;
    
    // 合并资源
    const mergedResources = mergeLocaleResources(locale, resources);
    
    // 缓存结果
    localeCache[locale] = mergedResources;
    
    return mergedResources;
  } catch (error) {
    console.warn(`Failed to load locale ${locale}, falling back to default locale`);
    
    // 降级到默认语言
    if (locale !== localeConfig.defaultLocale) {
      return loadLocaleResources(localeConfig.defaultLocale);
    }
    
    throw new Error(`Failed to load locale ${locale}`);
  }
}

// 获取翻译函数（根据当前语言和缓存资源；缺失 key 时回退到默认语言，确保提示语随用户切换的当前语言显示）
function getTranslator(locale: string) {
  return (key: string, params?: Record<string, any>): string => {
    const resources = localeCache[locale] || {};
    let translation = key.split('.').reduce((obj: any, k: string) => obj?.[k], resources);
    if (translation === undefined || translation === null || translation === key) {
      const defaultResources = localeCache[localeConfig.defaultLocale] || {};
      translation = key.split('.').reduce((obj: any, k: string) => obj?.[k], defaultResources) || key;
    }
    
    // 参数替换（undefined/null 转为空字符串，避免文案中出现 "undefined"）
    if (params && typeof translation === 'string') {
      Object.keys(params).forEach(paramKey => {
        const value = params[paramKey];
        const str = value === undefined || value === null ? '' : String(value);
        translation = translation.replace(new RegExp(`{{${paramKey}}}`, 'g'), str);
      });
    }
    
    return String(translation);
  };
}

// 获取翻译函数
export function useTranslation() {
  const t = (key: string, params?: Record<string, any>): string => {
    return getTranslator(currentLocale)(key, params);
  };

  const changeLocale = async (newLocale: string) => {
    try {
      await loadLocaleResources(newLocale);
      currentLocale = newLocale;
      
      // 更新文档语言属性
      document.documentElement.lang = newLocale;
      
      // 更新 RTL 属性
      const isRTL = localeConfig.rtlLocales.includes(newLocale as any);
      document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
      
      // 触发自定义事件
      window.dispatchEvent(new CustomEvent('localeChanged', { detail: { locale: newLocale } }));
      
      return true;
    } catch (error) {
      console.error('Failed to change locale:', error);
      return false;
    }
  };

  const getAvailableLanguages = () => {
    return localeConfig.supportedLocales;
  };

  const isRTL = () => {
    return localeConfig.rtlLocales.includes(currentLocale as any);
  };

  const getLocale = () => {
    return currentLocale;
  };

  return {
    t,
    changeLocale,
    getAvailableLanguages,
    isRTL,
    getLocale,
  };
}

// 语言码标准化：浏览器可能返回 "zh"（无区域）等，映射到已支持的语言
function normalizeLocale(locale: string): string {
  const normalized = locale.trim();
  if (normalized === 'zh' || normalized.toLowerCase() === 'zh') return 'zh-CN';
  return normalized;
}

// 初始化 i18n
export async function initI18n(initialLocale?: string) {
  // 先加载默认语言，确保缺失 key 时能回退到默认语言
  await loadLocaleResources(localeConfig.defaultLocale);
  
  const rawLocale = initialLocale ?? (typeof navigator !== 'undefined' ? navigator.language : localeConfig.defaultLocale);
  const browserLocale = normalizeLocale(rawLocale);
  
  try {
    await loadLocaleResources(browserLocale);
    currentLocale = browserLocale;
  } catch {
    // 尝试仅主语言码（如 zh-CN 失败时不再额外尝试 zh）
    currentLocale = localeConfig.defaultLocale;
  }
  
  document.documentElement.lang = currentLocale;
  
  // 设置 RTL 属性
  const isRTL = localeConfig.rtlLocales.includes(currentLocale as any);
  document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
  
  console.log(`i18n initialized with locale: ${currentLocale}`);
}

// 导出类型
export type TranslationKey = string;
export type LocaleParams = Record<string, string | number>;

// 创建并导出 i18n 对象
export const i18n = {
  t: (key: string, params?: Record<string, any>): string => {
    return getTranslator(currentLocale)(key, params);
  },
  changeLocale: async (newLocale: string) => {
    try {
      await loadLocaleResources(newLocale);
      currentLocale = newLocale;
      
      // 更新文档语言属性
      document.documentElement.lang = newLocale;
      
      // 更新 RTL 属性
      const isRTL = localeConfig.rtlLocales.includes(newLocale as any);
      document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
      
      // 触发自定义事件
      window.dispatchEvent(new CustomEvent('localeChanged', { detail: { locale: newLocale } }));
      
      return true;
    } catch (error) {
      console.error('Failed to change locale:', error);
      return false;
    }
  },
  getCurrentLocale: () => currentLocale,
  getAvailableLocales: () => localeConfig.supportedLocales,
  isRTL: () => localeConfig.rtlLocales.includes(currentLocale as any),
  init: initI18n,
};

// 导出辅助函数供其他模块使用
export function getAvailableLocales() {
  return localeConfig.supportedLocales;
}

export function setLocale(locale: string) {
  currentLocale = locale;
  document.documentElement.lang = locale;
  const isRTL = localeConfig.rtlLocales.includes(locale as any);
  document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
}

export function getCurrentLocale() {
  return currentLocale;
}

export { getTranslator };